/**
 * Motorberechnung (VB-26025, Blatt „Motorberechnung" der 2D-Vorlage).
 *
 * Drei Rechnungen und eine Auswahl:
 *
 * 1. **Drehmoment** — was der Räumer beim Ziehen der Ketten braucht: Gewicht der
 *    Balken + Gewicht der Ketten → Normalkraft → Reibkraft (Robalon auf Niro,
 *    µ = 0,25) → Moment am Förderkettenrad.
 * 2. **Abtriebsdrehzahl** aus der gewünschten Räumergeschwindigkeit — und die
 *    Umkehrung, weil das Blatt beide Richtungen führt (man kennt entweder die
 *    Sollgeschwindigkeit oder die Drehzahl des vorhandenen Getriebes).
 * 3. **Beckenoberfläche**, weil die Getriebetabellen daran hängen.
 * 4. **Motorauswahl**: drei Bauarten × fünf Stufen nach Beckenoberfläche, je mit
 *    maximalem Getriebemoment.
 *
 * **Zwei bewusste Abweichungen vom Blatt**, beide benannt statt versteckt:
 * - Das Blatt rechnet mit `3,1415`; hier steht `Math.PI` (Abweichung < 0,003 %).
 * - Für die Gliedlänge steht im Blatt `0,152` m, in der Kettenberechnung dagegen
 *   `152,4` mm. Hier gilt die genaue Gliedlänge, die auch die Kettenrechnung
 *   benutzt (Unterschied 0,26 % auf das Kettengewicht) — zwei Wahrheiten über
 *   dieselbe Kette wären schlimmer als der Rundungsunterschied.
 *
 * Reine Funktion, keine gespeicherten Ergebnisse — wie `calculateChain` und die
 * Gesamtmenge der Stückliste (ADR 0091).
 */

/** Erdbeschleunigung, wie das Blatt sie ansetzt: `Fg = m × 10`. */
const G = 10;

/** Balkentypen mit Metergewicht (Blatt A5:C7). */
export const FLIGHT_TYPES = {
  SIGMA_PRO_9: { label: 'Sigma Pro 9', weightPerMeterKg: 2.44 },
  HIGH_LOAD: { label: 'Hochlast', weightPerMeterKg: 3.67 },
  ULTRA_HIGH_LOAD: { label: 'Ultra Hochlast', weightPerMeterKg: 5.61 },
} as const;
export type FlightType = keyof typeof FLIGHT_TYPES;
export const FLIGHT_TYPE_KEYS = Object.keys(FLIGHT_TYPES) as FlightType[];

/** Kettentypen mit Gliedgewicht (Blatt A10:C11). */
export const MOTOR_CHAIN_TYPES = {
  NCS: { label: 'NCS', weightPerLinkKg: 0.3 },
  HS_730: { label: 'HS 730', weightPerLinkKg: 0.4 },
} as const;
export type MotorChainType = keyof typeof MOTOR_CHAIN_TYPES;
export const MOTOR_CHAIN_TYPE_KEYS = Object.keys(MOTOR_CHAIN_TYPES) as MotorChainType[];

/** Reibungskoeffizient Robalon–Niro (Blatt B22). */
export const DEFAULT_FRICTION_COEFFICIENT = 0.25;

export interface MotorCalcInput {
  /** Beckenlänge in m (B14) — steht am Projekt. */
  basinLengthM: number;
  /** Beckenbreite in m (B15) — steht am Projekt. */
  basinWidthM: number;
  /** Länge eines Balkens in m (B16) — Ablängung der Auslegungszeile. */
  flightLengthM: number;
  /** Balkenanzahl (B17) — Stückzahl der Balken-Zeile (ADR 0094). */
  flightCount: number;
  /** Kettenlänge in m (B18) — aus der Kettenberechnung. */
  chainLengthM: number;
  /** Teilkreisdurchmesser des Förderkettenrads in m (B19). */
  sprocketDiameterM: number;
  /** Länge eines Kettengliedes in mm — dieselbe Zahl wie in der Kettenberechnung. */
  chainLinkLengthMm: number;
  flightType: FlightType;
  chainType: MotorChainType;
  /** Reibungskoeffizient (B22); Vorlage 0,25. */
  frictionCoefficient: number;
  /** Zähne des Antriebsritzels (B55). */
  pinionTeeth: number;
  /** Zähne des Antriebskettenrads (B56). */
  sprocketTeeth: number;
  /** Teilkreis des Förderkettenrads in cm (B54) — die Tabelle rechnet in cm. */
  sprocketPitchCm: number;
  /** Gewünschte Räumergeschwindigkeit in cm/s (B57); Vorlage 2,4. */
  scraperSpeedCmS: number;
}

/** Ein Vorschlag aus einer der drei Getriebetabellen. */
export interface GearboxOption {
  kind: GearboxKind;
  /** Obergrenze der Stufe in m² (`null` = „ab da"). */
  maxAreaM2: number | null;
  maxTorqueNm: number;
  gearbox: string;
  motor: string;
  power: string;
  speedRpm: number;
  poles: number;
  /** Reicht das Getriebemoment für das gerechnete Moment? */
  isTorqueSufficient: boolean;
}

export type GearboxKind = 'SPUR' | 'WORM' | 'BEVEL';

export const GEARBOX_KIND_LABELS: Record<GearboxKind, string> = {
  SPUR: 'Stirnradgetriebe',
  WORM: 'Schneckenradgetriebe',
  BEVEL: 'Kegelradgetriebe (auf Anfrage)',
};

interface GearboxRow {
  maxAreaM2: number | null;
  maxTorqueNm: number;
  gearbox: string;
  motor: string;
  power: string;
  speedRpm: number;
  poles: number;
}

/**
 * Die Tabellen des Blattes (A87:I107), unverändert übernommen. Geschlossene
 * Listen statt freier Eingabe: Was SEW liefert, entscheidet nicht das Formular.
 */
export const MOTOR_GEARBOXES: Record<GearboxKind, GearboxRow[]> = {
  SPUR: [
    { maxAreaM2: 50, maxTorqueNm: 200, gearbox: 'R37R17', motor: 'DR63L6', power: '0,18 kW', speedRpm: 1000, poles: 6 },
    { maxAreaM2: 200, maxTorqueNm: 450, gearbox: 'R57R37', motor: 'DR63L6', power: '0,18 kW', speedRpm: 1000, poles: 6 },
    { maxAreaM2: 400, maxTorqueNm: 600, gearbox: 'R67R37', motor: 'DRS71S6', power: '0,25 kW', speedRpm: 1000, poles: 6 },
    { maxAreaM2: 600, maxTorqueNm: 1150, gearbox: 'R87R57', motor: 'DRS71S6', power: '0,25 kW', speedRpm: 1000, poles: 6 },
    { maxAreaM2: null, maxTorqueNm: 3000, gearbox: 'R97R57', motor: 'DRS71M6', power: '0,37 kW', speedRpm: 1000, poles: 6 },
  ],
  WORM: [
    { maxAreaM2: 50, maxTorqueNm: 185, gearbox: 'SA47R17/T', motor: 'DR63L6', power: '0,18 kW', speedRpm: 1000, poles: 6 },
    { maxAreaM2: 200, maxTorqueNm: 300, gearbox: 'SA57R17/T', motor: 'DR63L6', power: '0,18 kW', speedRpm: 1000, poles: 6 },
    { maxAreaM2: 400, maxTorqueNm: 570, gearbox: 'SA67R37/T', motor: 'DRS71S6', power: '0,25 kW', speedRpm: 1000, poles: 6 },
    { maxAreaM2: 600, maxTorqueNm: 1270, gearbox: 'SA77R37/T', motor: 'DRS71S6', power: '0,25 kW', speedRpm: 1000, poles: 6 },
    { maxAreaM2: null, maxTorqueNm: 2500, gearbox: 'SA87R37/T', motor: 'DRS71M6', power: '0,37 kW', speedRpm: 1000, poles: 6 },
  ],
  BEVEL: [
    { maxAreaM2: 50, maxTorqueNm: 200, gearbox: 'KA37R17/T', motor: 'DR63L6', power: '0,18 kW', speedRpm: 1000, poles: 6 },
    { maxAreaM2: 200, maxTorqueNm: 400, gearbox: 'KA47R37/T', motor: 'DR63L6', power: '0,18 kW', speedRpm: 1000, poles: 6 },
    { maxAreaM2: 400, maxTorqueNm: 600, gearbox: 'KA57R37/T', motor: 'DRS71S6', power: '0,25 kW', speedRpm: 1000, poles: 6 },
    { maxAreaM2: 600, maxTorqueNm: 1550, gearbox: 'KA77R37/T', motor: 'DRS71S6', power: '0,25 kW', speedRpm: 1000, poles: 6 },
    { maxAreaM2: null, maxTorqueNm: 4300, gearbox: 'KA97R57/T', motor: 'DRS71M6', power: '0,37 kW', speedRpm: 1000, poles: 6 },
  ],
};

export interface MotorCalcResult {
  /** Gewicht eines Balkens in kg (`m Balken × L Balken`). */
  flightWeightKg: number;
  /** Kettengewicht je Balkenabstand in kg. */
  chainWeightKg: number;
  /** Normalkraft in N — `(Balken + Kette) × 10`. */
  normalForceN: number;
  /** Reibkraft je Balken in N. */
  frictionPerFlightN: number;
  /** Reibkraft gesamt in N (= Zugkraft). */
  frictionTotalN: number;
  /** Drehmoment am Förderkettenrad in Nm — die Zahl für die Getriebeauswahl. */
  torqueNm: number;
  /** Beckenoberfläche in m². */
  basinAreaM2: number;
  /** Abtriebsdrehzahl in U/min aus der gewünschten Geschwindigkeit. */
  outputSpeedRpm: number;
  /** Je Bauart der erste passende Vorschlag; `null`, wenn keiner reicht. */
  gearboxes: Record<GearboxKind, GearboxOption | null>;
  /** Was fehlt oder nicht aufgeht — benannt, nicht verschluckt. */
  issues: string[];
}

function round(value: number, digits = 4): number {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

/** Zahl für eine Meldung, die als fertiger Satz in der Oberfläche landet. */
function de(value: number): string {
  return value.toLocaleString('de-DE', { maximumFractionDigits: 1 });
}

/**
 * Räumergeschwindigkeit → Abtriebsdrehzahl (Blatt D62) und zurück (D78).
 *
 * `n = (v × 60) / (Tk × π × (z1 / z2))`, mit `Tk` in cm und `v` in cm/s.
 */
export function outputSpeedFromScraperSpeed(input: {
  scraperSpeedCmS: number;
  sprocketPitchCm: number;
  pinionTeeth: number;
  sprocketTeeth: number;
}): number {
  const { scraperSpeedCmS, sprocketPitchCm, pinionTeeth, sprocketTeeth } = input;
  if (sprocketPitchCm <= 0 || sprocketTeeth <= 0) return 0;
  return round((scraperSpeedCmS * 60) / (sprocketPitchCm * Math.PI * (pinionTeeth / sprocketTeeth)));
}

/** Die Umkehrung: aus der Drehzahl des Getriebes die Geschwindigkeit (Blatt D78). */
export function scraperSpeedFromOutputSpeed(input: {
  outputSpeedRpm: number;
  sprocketPitchCm: number;
  pinionTeeth: number;
  sprocketTeeth: number;
}): number {
  const { outputSpeedRpm, sprocketPitchCm, pinionTeeth, sprocketTeeth } = input;
  if (sprocketTeeth <= 0) return 0;
  return round((sprocketPitchCm * Math.PI * outputSpeedRpm * (pinionTeeth / sprocketTeeth)) / 60);
}

/**
 * Der erste Vorschlag einer Bauart, dessen **Stufe** zur Beckenoberfläche passt.
 *
 * Reicht dessen Moment nicht, wird die nächstgrößere Stufe genommen und das
 * vermerkt (`isTorqueSufficient`) — das Blatt lässt diese Entscheidung offen, aber
 * ein Getriebe, das zu schwach ist, ist keine Auswahl.
 */
export function selectGearbox(
  kind: GearboxKind,
  areaM2: number,
  torqueNm: number,
): GearboxOption | null {
  const rows = MOTOR_GEARBOXES[kind];
  const byArea = rows.findIndex((row) => row.maxAreaM2 == null || areaM2 <= row.maxAreaM2);
  if (byArea < 0) return null;
  for (let index = byArea; index < rows.length; index += 1) {
    if (rows[index].maxTorqueNm >= torqueNm) {
      return { kind, ...rows[index], isTorqueSufficient: true };
    }
  }
  // Nichts reicht: den größten nennen und sagen, dass er nicht reicht.
  const last = rows[rows.length - 1];
  return { kind, ...last, isTorqueSufficient: false };
}

export function calculateMotor(input: MotorCalcInput): MotorCalcResult {
  const issues: string[] = [];
  const {
    basinLengthM,
    basinWidthM,
    flightLengthM,
    flightCount,
    chainLengthM,
    sprocketDiameterM,
    chainLinkLengthMm,
    flightType,
    chainType,
    frictionCoefficient,
    pinionTeeth,
    sprocketTeeth,
    sprocketPitchCm,
    scraperSpeedCmS,
  } = input;

  const flightWeightKg = round(FLIGHT_TYPES[flightType].weightPerMeterKg * flightLengthM);

  /**
   * Kettengewicht je Balkenabstand: `Kettenlänge / Balkenanzahl / Gliedlänge`
   * Glieder, jedes mit seinem Gewicht. Die Gliedlänge kommt in m in die Rechnung.
   */
  const chainLinkLengthM = chainLinkLengthMm / 1000;
  let chainWeightKg = 0;
  if (flightCount > 0 && chainLinkLengthM > 0) {
    chainWeightKg = round((chainLengthM / flightCount / chainLinkLengthM) * MOTOR_CHAIN_TYPES[chainType].weightPerLinkKg);
  } else {
    issues.push('Ohne Balkenanzahl und Gliedlänge lässt sich das Kettengewicht nicht rechnen.');
  }

  const normalForceN = round((flightWeightKg + chainWeightKg) * G);
  const frictionPerFlightN = round(frictionCoefficient * normalForceN);
  const frictionTotalN = round(frictionPerFlightN * flightCount);
  const torqueNm = round((frictionTotalN * sprocketDiameterM) / 2);
  const basinAreaM2 = round(basinLengthM * basinWidthM);
  const outputSpeedRpm = outputSpeedFromScraperSpeed({
    scraperSpeedCmS,
    sprocketPitchCm,
    pinionTeeth,
    sprocketTeeth,
  });

  if (flightLengthM <= 0) issues.push('Die Balkenlänge fehlt — ohne sie ist das Balkengewicht 0.');
  if (sprocketDiameterM <= 0) issues.push('Der Durchmesser des Förderkettenrads fehlt — ohne ihn gibt es kein Moment.');
  if (basinAreaM2 <= 0) issues.push('Beckenlänge und -breite fehlen — die Getriebetabelle hängt an der Oberfläche.');
  if (outputSpeedRpm <= 0) issues.push('Zähnezahlen oder Räumergeschwindigkeit fehlen — keine Abtriebsdrehzahl.');

  const gearboxes: Record<GearboxKind, GearboxOption | null> = {
    SPUR: basinAreaM2 > 0 ? selectGearbox('SPUR', basinAreaM2, torqueNm) : null,
    WORM: basinAreaM2 > 0 ? selectGearbox('WORM', basinAreaM2, torqueNm) : null,
    BEVEL: basinAreaM2 > 0 ? selectGearbox('BEVEL', basinAreaM2, torqueNm) : null,
  };
  for (const option of Object.values(gearboxes)) {
    if (option && !option.isTorqueSufficient) {
      issues.push(
        `${GEARBOX_KIND_LABELS[option.kind]}: Das größte Getriebe der Tabelle (${option.gearbox}, ` +
          `${de(option.maxTorqueNm)} Nm) liegt unter dem gerechneten Moment von ${de(torqueNm)} Nm.`,
      );
    }
  }

  return {
    flightWeightKg,
    chainWeightKg,
    normalForceN,
    frictionPerFlightN,
    frictionTotalN,
    torqueNm,
    basinAreaM2,
    outputSpeedRpm,
    gearboxes,
    issues,
  };
}
